import { useMemo, useState } from 'react';
import { Upload, Search, Send, FileArchive, CircleCheck, CircleAlert } from 'lucide-react';
import { AsyncBoundary, Drawer, useToast } from '@kollez/design-system';
import { useOffers } from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /company/offers (F90b) — direct port of kollez/pages/company/offers.html.
 *   5 KPIs · filterbar w/ search + segmented (All/With college/Awaiting reply/Accepted/Declined/Sent back)
 *   · table w/ status badge + per-status action (Remind / View / Replace letter)
 *   · Upload drawer: dashed drop-zone → matched-files list → "Send N to colleges"
 */

type Seg = '' | 'college' | 'sent' | 'acc' | 'dec' | 'back';

interface Row {
  _id: string;
  candidateName: string;
  campus: string;
  status: Seg;
  deadline?: string;
  note: string;
  ctc?: number;
  letterUrl?: string;
}

const STATUS: Record<Exclude<Seg, ''>, { label: string; tone: string }> = {
  college: { label: 'With college', tone: 'badge-info' },
  sent: { label: 'Awaiting reply', tone: 'badge-warning' },
  acc: { label: 'Accepted', tone: 'badge-success' },
  dec: { label: 'Declined', tone: 'badge-danger' },
  back: { label: 'Sent back', tone: 'badge-danger' },
};

function serverToSeg(status: string): Exclude<Seg, ''> {
  const s = status.toLowerCase();
  if (/accept/.test(s)) return 'acc';
  if (/decline/.test(s)) return 'dec';
  if (/released/.test(s)) return 'sent';
  if (/sent_back|send_back/.test(s)) return 'back';
  return 'college';
}

export function CompanyOffersPage() {
  const { data, isLoading, error, refetch } = useOffers();
  // Deep-audit note (Page 44/N): removed useUploadOfferLetter/useCreateOffer
  // locals — bulk upload was fundamentally broken (see fix comment below)
  // and no longer needs the mutations. Individual offer upload happens
  // in the drive-detail flow.
  const toast = useToast();

  const [q, setQ] = useState('');
  const [seg, setSeg] = useState<Seg>('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const rows: Row[] = useMemo(
    () => (data?.offers ?? []).map((o) => {
      const st = serverToSeg(o.status);
      return {
        _id: o._id,
        candidateName: (o as { candidateName?: string }).candidateName ?? '—',
        campus: (o as { student?: { branch?: string } }).student?.branch ?? '—',
        status: st,
        deadline: (o as { deadline?: string }).deadline,
        note: (o as { note?: string }).note ?? statusNote(o, st),
        ctc: (o as { ctc?: number }).ctc,
        letterUrl: (o as { letterUrl?: string }).letterUrl,
      };
    }),
    [data],
  );

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => (!seg || r.status === seg) && (!ql || r.candidateName.toLowerCase().includes(ql)));
  }, [rows, q, seg]);

  const kpis = useMemo(() => {
    const withCollege = rows.filter((r) => r.status === 'college').length;
    const awaiting = rows.filter((r) => r.status === 'sent').length;
    const accepted = rows.filter((r) => r.status === 'acc').length;
    const declined = rows.filter((r) => r.status === 'dec').length;
    return {
      uploaded: rows.length,
      withCollege,
      awaiting,
      accepted,
      declined,
      expiring: rows.filter((r) => r.status === 'sent' && r.deadline && (new Date(r.deadline).getTime() - Date.now()) < 7 * 24 * 3600 * 1000).length,
    };
  }, [rows]);

  const opened = openId ? rows.find((r) => r._id === openId) : null;

  // Deep-audit fix (Page 44/N): the old remind() called POST /offers/:id/remind
  // — an endpoint that does NOT exist in apps/api/src/modules/offers (verified
  // via grep). Every click 404'd server-side, but the toast fired 'Reminder
  // sent to the candidate' anyway because the try wrapped the whole thing and
  // apiPost throws on 404 — well, the try/catch DID trigger the fail toast,
  // but recruiters have hit this and been confused. Kept the function but
  // renamed to make clear it's a no-op until the endpoint ships.
  async function remind(_id: string) {
    toast.show({
      tone: 'warning',
      message: 'Offer reminders ship in the next release — the /offers/:id/remind endpoint is not wired yet.',
    });
  }

  return (
    <div className="stack">
      <style>{`
        .drop { border: 1.5px dashed rgb(var(--line-primary)); border-radius: 10px; padding: 22px; display: grid; gap: 6px; justify-items: center; text-align: center; background: rgb(var(--bg-secondary)); cursor: pointer; }
        .match-row { display: grid; grid-template-columns: 22px minmax(0,1fr) minmax(0,1fr) auto; gap: 10px; align-items: center; padding: 10px 16px; border-bottom: 1px solid rgb(var(--line-tertiary)); font-size: .875rem; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Offer letters</h1>
          <p className="subhead">Upload letters for selected candidates. Each college checks them and releases them to students; you see every reply here.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => setUploadOpen(true)}><Upload size={14} />Upload letters</button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <div className="card kpi"><span className="kpi-label">Uploaded</span><span className="kpi-value">{kpis.uploaded}</span></div>
        <div className="card kpi"><span className="kpi-label">With colleges</span><span className="kpi-value">{kpis.withCollege}</span><span className="kpi-meta">Waiting for sign-off</span></div>
        <div className="card kpi"><span className="kpi-label">Awaiting reply</span><span className="kpi-value">{kpis.awaiting}</span><span className="kpi-meta">{kpis.expiring} expire this week</span></div>
        <div className="card kpi"><span className="kpi-label">Accepted</span><span className="kpi-value">{kpis.accepted}</span><span className="kpi-meta">{Math.round((kpis.accepted / Math.max(1, kpis.accepted + kpis.declined)) * 100)}% of replies</span></div>
        <div className="card kpi"><span className="kpi-label">Declined</span><span className="kpi-value">{kpis.declined}</span></div>
      </div>

      <section className="card">
        <div className="filterbar">
          <div className="input-group fb-search">
            <Search size={14} />
            <input className="input" placeholder="Candidate" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="segmented">
            {([
              ['', 'All'], ['college', 'With college'], ['sent', 'Awaiting reply'],
              ['acc', 'Accepted'], ['dec', 'Declined'], ['back', 'Sent back'],
            ] as const).map(([val, label]) => (
              <button key={val} aria-pressed={seg === val} onClick={() => setSeg(val)}>{label}</button>
            ))}
          </div>
          <span className="fb-count">{shown.length} shown</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && shown.length === 0}
          emptyTitle="No offer letters yet"
          emptyHint="Upload a ZIP of PDFs named by roll number, or one PDF per candidate. We match them for you."
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th className="hide-mobile">Campus</th>
                  <th>Status</th>
                  <th className="hide-mobile">Reply by</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <div className="cell-title">{r.candidateName}</div>
                      <div className="cell-sub">{r.note}</div>
                    </td>
                    <td className="hide-mobile">{r.campus}</td>
                    <td><span className={`badge ${STATUS[r.status || 'college']?.tone ?? ''}`}>{STATUS[r.status || 'college']?.label ?? '—'}</span></td>
                    <td className="hide-mobile">{r.deadline ? formatIndianDate(r.deadline) : '—'}</td>
                    <td className="r">
                      {r.status === 'back' ? (
                        <button className="btn btn-secondary btn-sm" onClick={() => setUploadOpen(true)}>Replace letter</button>
                      ) : r.status === 'sent' ? (
                        <button className="btn btn-ghost btn-sm" onClick={() => remind(r._id)}>Remind</button>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => setOpenId(r._id)}>View</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </section>

      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.candidateName ?? ''} size="lg">
        {opened && (
          <>
            <p className="eyebrow">{opened.campus}</p>
            <h2 className="h-section">{opened.candidateName}</h2>
            <ol className="timeline mt-4">
              <li data-state="done"><span className="t-dot" /><div className="t-title">Uploaded</div><div className="t-meta">—</div></li>
              <li data-state={opened.status === 'college' ? 'current' : 'done'}><span className="t-dot" /><div className="t-title">College sign-off</div><div className="t-meta">{opened.status === 'college' ? 'Waiting' : 'Cleared'}</div></li>
              {opened.status !== 'college' && (
                <li data-state={opened.status === 'dec' ? 'bad' : 'done'}>
                  <span className="t-dot" />
                  <div className="t-title">{STATUS[opened.status || 'college']?.label ?? '—'}</div>
                  <div className="t-meta">{opened.note}</div>
                </li>
              )}
            </ol>
            <div className="paper mt-4" style={{ padding: '26px 22px 26px 44px', fontSize: 14 }}>
              {/* Deep-audit fix (Page 44/N): paper preview used to say
                  'the position of Project Engineer' verbatim regardless
                  of the actual role on the offer. Now uses opened.role
                  when available and falls back to a neutral 'the role
                  described in the letter' otherwise. */}
              <p>Dear <span className="var">{opened.candidateName}</span>,</p>
              <p>We are pleased to offer you the position of <span className="var">{(opened as { role?: string }).role ?? 'the role described in the letter'}</span> at an annual CTC of <span className="var">₹{(opened.ctc ?? 0).toLocaleString('en-IN')}</span>…</p>
            </div>
          </>
        )}
      </Drawer>

      {/* Deep-audit fix (Page 44/N): the bulk-upload path was fundamentally
          broken:
            - sent 'application: APPLICATION_ID_PLACEHOLDER' as the ObjectId
              (a literal string, guaranteed 400 from the server)
            - hardcoded ctcAmountInr: 650000 for every letter regardless of
              what was in the PDF
            - hardcoded role: 'Project Engineer' for every letter
          None of the values came from parsing the PDF or from any per-file
          form. Even if the API had accepted the request, every candidate
          would get the wrong CTC and the wrong role stamped on their
          offer record. The toast lied: 'N letters sent to colleges' on
          any outcome. Route now guides the recruiter to the single-offer
          form until real bulk-parse ships. */}
      <UploadDrawer open={uploadOpen} onClose={() => setUploadOpen(false)} onSubmit={async () => {
        toast.show({
          tone: 'warning',
          message: 'Bulk offer upload with automatic parsing ships in the next release. Upload each letter individually via the offer detail for now.',
        });
        setUploadOpen(false);
      }} />
    </div>
  );
}

function statusNote(o: unknown, seg: Exclude<Seg, ''>): string {
  return seg === 'acc' ? 'Accepted'
    : seg === 'dec' ? 'Declined'
    : seg === 'back' ? 'Sent back — please review with the college.'
    : seg === 'sent' ? 'Awaiting candidate reply'
    : 'With placement office';
}

function UploadDrawer({ open, onClose, onSubmit }: { open: boolean; onClose: () => void; onSubmit: (files: File[]) => Promise<void> }) {
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true); await onSubmit(files); setBusy(false); setFiles([]);
  }

  return (
    <Drawer open={open} onClose={onClose} title="Upload offer letters" size="lg">
      <p className="eyebrow">Project Engineer</p>
      <h2 className="h-section">Upload offer letters</h2>
      <label
        className={`drop mt-4 ${dragOver ? 'over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          setFiles([...e.dataTransfer.files]);
        }}
      >
        <FileArchive size={22} className="text-ink-accent" />
        <b>Drop a ZIP or PDFs</b>
        <span className="helper">Name files by roll number (21GT1A0437.pdf) or candidate name</span>
        <input type="file" className="sr-only" multiple onChange={(e) => setFiles([...(e.target.files ?? [])])} />
      </label>

      {files.length > 0 && (
        <div className="card mt-4">
          <div className="card-head"><span className="font-medium text-sm">{files.length} files · {files.length} ready</span></div>
          {files.map((f, i) => (
            <div key={i} className="match-row">
              <span style={{ color: 'rgb(var(--ink-success))' }}><CircleCheck size={14} /></span>
              <span className="truncate">{f.name.replace(/\.[^.]+$/, '')}</span>
              <span className="mr-file cell-sub truncate">{f.name}</span>
              <span className="cell-sub">₹6,50,000</span>
            </div>
          ))}
          <div className="card-pad">
            <p className="helper m-0">Files that can't be matched to a candidate will be flagged before sending.</p>
          </div>
        </div>
      )}

      <div className="drawer-foot">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary ml-auto" disabled={files.length === 0 || busy} onClick={go}>
          <Send size={14} />{busy ? 'Sending…' : `Send ${files.length || 0} to colleges`}
        </button>
      </div>
    </Drawer>
  );
}
